<!--
  ~ Copyright (c) 2023 D2ArmorPicker by Mijago.
  ~
  ~ This program is free software: you can redistribute it and/or modify
  ~ it under the terms of the GNU Affero General Public License as published
  ~ by the Free Software Foundation, either version 3 of the License, or
  ~ (at your option) any later version.
  ~
  ~ This program is distributed in the hope that it will be useful,
  ~ but WITHOUT ANY WARRANTY; without even the implied warranty of
  ~ MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  ~ GNU Affero General Public License for more details.
  ~
  ~ You should have received a copy of the GNU Affero General Public License
  ~ along with this program.  If not, see <http://www.gnu.org/licenses/>.
  -->

<div class="card-view-container">
  <!-- Filters and Sort Controls -->
  <div class="filters-container" *ngIf="filteredResults.length > 0">
    <div class="results-count">
      Showing {{ displayedResults.length }} of {{ filteredResults.length }} results
    </div>

    <div class="sort-controls">
      <span class="sort-label">Sort by:</span>

      <button
        mat-icon-button
        class="sort-button"
        [class.active]="activeSortField === 'total'"
        (click)="toggleSort('total')"
        matTooltip="Sort by Total Stats">
        <span class="sort-text">Total</span>
        <mat-icon class="sort-icon">{{ getSortIcon("total") }}</mat-icon>
      </button>

      <button
        mat-icon-button
        class="sort-button"
        [class.active]="activeSortField === 'mods'"
        (click)="toggleSort('mods')"
        matTooltip="Sort by Mod Cost">
        <span class="sort-text">Mods</span>
        <mat-icon class="sort-icon">{{ getSortIcon("mods") }}</mat-icon>
      </button>

      <button
        mat-icon-button
        class="sort-button"
        [class.active]="activeSortField === 'gearsetBonuses'"
        (click)="toggleSort('gearsetBonuses')"
        matTooltip="Sort by Gearset Bonuses">
        <span class="sort-text">Gearset</span>
        <mat-icon class="sort-icon">{{ getSortIcon("gearsetBonuses") }}</mat-icon>
      </button>

      <button
        mat-icon-button
        class="sort-button"
        [class.active]="activeSortField === 'health'"
        (click)="toggleSort('health')"
        matTooltip="Sort by Health">
        <img [src]="getStatIcon(1)" alt="Health" class="sort-stat-icon" />
        <mat-icon class="sort-icon">{{ getSortIcon("health") }}</mat-icon>
      </button>

      <button
        mat-icon-button
        class="sort-button"
        [class.active]="activeSortField === 'melee'"
        (click)="toggleSort('melee')"
        matTooltip="Sort by Melee">
        <img [src]="getStatIcon(5)" alt="Melee" class="sort-stat-icon" />
        <mat-icon class="sort-icon">{{ getSortIcon("melee") }}</mat-icon>
      </button>

      <button
        mat-icon-button
        class="sort-button"
        [class.active]="activeSortField === 'grenade'"
        (click)="toggleSort('grenade')"
        matTooltip="Sort by Grenade">
        <img [src]="getStatIcon(3)" alt="Grenade" class="sort-stat-icon" />
        <mat-icon class="sort-icon">{{ getSortIcon("grenade") }}</mat-icon>
      </button>

      <button
        mat-icon-button
        class="sort-button"
        [class.active]="activeSortField === 'super'"
        (click)="toggleSort('super')"
        matTooltip="Sort by Super">
        <img [src]="getStatIcon(4)" alt="Super" class="sort-stat-icon" />
        <mat-icon class="sort-icon">{{ getSortIcon("super") }}</mat-icon>
      </button>

      <button
        mat-icon-button
        class="sort-button"
        [class.active]="activeSortField === 'class'"
        (click)="toggleSort('class')"
        matTooltip="Sort by Class">
        <img [src]="getStatIcon(2)" alt="Class" class="sort-stat-icon" />
        <mat-icon class="sort-icon">{{ getSortIcon("class") }}</mat-icon>
      </button>

      <button
        mat-icon-button
        class="sort-button"
        [class.active]="activeSortField === 'weapon'"
        (click)="toggleSort('weapon')"
        matTooltip="Sort by Weapon">
        <img [src]="getStatIcon(0)" alt="Weapon" class="sort-stat-icon" />
        <mat-icon class="sort-icon">{{ getSortIcon("weapon") }}</mat-icon>
      </button>
    </div>
  </div>

  <!-- Cards Container -->
  <div class="cards-container" (scroll)="onScroll($event)">
    <div class="card-grid">
      <mat-card
        *ngFor="let result of displayedResults; let i = index; trackBy: trackByFn"
        class="result-card"
        [class.expanded]="expandedCard === i"
        (click)="toggleCard(i)"
        appearance="outlined">
        <!-- Compact View -->
        <div class="card-compact" [class.hidden]="expandedCard === i">
          <!-- First Row: Exotic, Stats, Mods, Total -->
          <div class="card-header">
            <div class="identity-section">
              <div class="exotic-section">
                <div class="exotic-icon">
                  <app-item-icon
                    [itemHash]="$any(result.exotic.hash)"
                    *ngIf="result.exotic"
                    [matTooltip]="result.exotic.name"
                    class="exotic-img" />
                  <img
                    *ngIf="!result.exotic"
                    src="https://www.bungie.net/common/destiny2_content/icons/b4d05ef69d0c3227a7d4f7f35bbc2848.png"
                    alt="No Exotic"
                    class="exotic-img" />
                </div>
                <span
                  class="exotic-name"
                  [matTooltip]="result.exotic?.name || 'No Exotic'"
                  [matTooltipDisabled]="!shouldShowTooltip(result.exotic?.name || 'No Exotic')">
                  {{ result.exotic?.name || "No Exotic" }}
                </span>
              </div>

              <div class="gearset-section">
                <span
                  *ngFor="let bonus of getGearsetBonuses(result)"
                  [gearsetTooltip]="bonus.perk"
                  class="gearset-bonus">
                  <img
                    *ngIf="bonus.perk.displayProperties.hasIcon"
                    [alt]="bonus.perk.displayProperties.name"
                    [src]="'https://bungie.net/' + bonus.perk.displayProperties.icon"
                    class="gearset-bonus-icon"
                    loading="lazy" />
                  <span class="gearset-bonus-count">{{ bonus.requiredSetCount }}</span>
                </span>
              </div>
            </div>

            <div class="stats-section">
              <div
                class="stat-item-compact"
                *ngFor="let stat of STAT_ORDER; let statIndex = index"
                [matTooltip]="getStatName(stat)">
                <img
                  [src]="getStatIcon(stat)"
                  [alt]="getStatName(stat)"
                  class="stat-icon-compact" />
                <span class="stat-value-compact">{{ result.stats[stat] }}</span>
              </div>
            </div>
            <div class="mods-section">
              <app-table-mod-display
                [mods]="result.mods"
                [artifice]="result.artifice"
                [tuningMods]="result.tuningMods"
                class="mods-display">
              </app-table-mod-display>
            </div>

            <div class="tiers-section">
              <span class="total-stats">{{ getTotalStats(result) }}</span>
              <span class="tiers-label">Total</span>
            </div>
          </div>

          <!-- Sources Row -->
          <div class="sources-row" *ngIf="result.usesCollectionRoll || result.usesVendorRoll">
            <mat-chip-listbox>
              <mat-chip-option *ngIf="result.usesCollectionRoll" disabled>
                <mat-icon>collections_bookmark</mat-icon>
                Collection
              </mat-chip-option>
              <mat-chip-option *ngIf="result.usesVendorRoll" disabled>
                <mat-icon>store</mat-icon>
                Vendor
              </mat-chip-option>
            </mat-chip-listbox>
          </div>
        </div>

        <!-- Expanded View -->
        <div class="card-expanded" [class.hidden]="expandedCard !== i">
          <div class="expanded-content">
            <!-- Left Side: Summary -->
            <div class="left-summary">
              <div class="exotic-section-expanded">
                <app-item-icon
                  [itemHash]="$any(result.exotic.hash)"
                  *ngIf="result.exotic"
                  [matTooltip]="result.exotic.name"
                  class="exotic-img-large" />

                <div class="exotic-info">
                  <h3>{{ result.exotic?.name || "No Exotic" }}</h3>
                  <div class="total-stats-large">{{ getTotalStats(result) }} Total</div>
                </div>
              </div>

              <div class="gearset-section-expanded" *ngIf="getGearsetBonuses(result).length > 0">
                <h4>Gearset Bonuses</h4>
                <div class="gearset-section">
                  <span
                    *ngFor="let bonus of getGearsetBonuses(result)"
                    [gearsetTooltip]="bonus.perk"
                    class="gearset-bonus">
                    <img
                      *ngIf="bonus.perk.displayProperties.hasIcon"
                      [alt]="bonus.perk.displayProperties.name"
                      [src]="'https://bungie.net/' + bonus.perk.displayProperties.icon"
                      class="gearset-bonus-icon"
                      loading="lazy" />
                    <span class="gearset-bonus-count">{{ bonus.requiredSetCount }}</span>
                  </span>
                </div>
              </div>

              <div class="mods-section-expanded">
                <h4>Required Mods</h4>
                <app-table-mod-display
                  [mods]="result.mods"
                  [artifice]="result.artifice"
                  [tuningMods]="result.tuningMods">
                </app-table-mod-display>
              </div>

              <div class="stats-summary">
                <h4>Final Stats</h4>
                <div class="stats-grid">
                  <div
                    class="stat-item-large"
                    *ngFor="let stat of STAT_ORDER"
                    [matTooltip]="getStatName(stat)">
                    <img
                      [src]="getStatIcon(stat)"
                      [alt]="getStatName(stat)"
                      class="stat-icon-large" />
                    <div class="stat-info">
                      <span class="stat-value-large">{{ result.stats[stat] }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Side: Detailed Breakdown -->
            <div class="right-details">
              <h4>Armor Breakdown</h4>
              <div class="armor-table">
                <table class="breakdown-table">
                  <thead>
                    <tr>
                      <th>Piece</th>
                      <th *ngFor="let stat of STAT_ORDER">
                        {{ ArmorStatNames[stat] }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let item of result.items" class="armor-row">
                      <td class="item-name">
                        <div class="item-info">
                          <app-item-icon
                            [itemHash]="item.hash"
                            [masterworked]="item.masterworked"
                            [source]="item.source">
                          </app-item-icon>
                          <span
                            class="item-name-text"
                            [matTooltip]="item.name"
                            [matTooltipDisabled]="!shouldShowTooltip(item.name)">
                            {{ getShortItemName(item.name) }}
                          </span>

                          <!-- Masterwork level indicator -->
                          <span
                            *ngIf="getMasterworkDisplay(item)"
                            class="masterwork-level"
                            [class.armor-v3]="item.armorSystem === 3"
                            [class.armor-legacy]="item.armorSystem < 3"
                            [class.fully-masterworked]="
                              item.armorSystem === 3 && item.masterworkLevel === 5
                            "
                            [matTooltip]="getMasterworkTooltip(item)">
                            {{ getMasterworkDisplay(item) }}
                          </span>

                          <!-- Socket/Perk indicator -->
                          <div class="socket-container" *ngIf="item.perk">
                            <app-armor-perk-icon [perk]="item.perk || 0"></app-armor-perk-icon>
                          </div>

                          <!-- Transfer state indicators -->
                          <div class="transfer-state-indicators">
                            <!-- Loading spinner for waiting/transferring states -->
                            <mat-progress-spinner
                              *ngIf="item.transferState === 1 || item.transferState === 2"
                              class="transfer-spinner"
                              color="primary"
                              diameter="16"
                              mode="indeterminate">
                            </mat-progress-spinner>

                            <!-- Success icon -->
                            <mat-icon
                              *ngIf="item.transferState === 3"
                              class="transfer-success"
                              matTooltip="Item successfully moved to inventory.">
                              check_circle_outline
                            </mat-icon>

                            <!-- Error icon -->
                            <mat-icon
                              *ngIf="item.transferState === 4"
                              class="transfer-error"
                              matTooltip="This item could not be moved. Make sure that there is enough space on your character.">
                              error_outline
                            </mat-icon>
                          </div>
                        </div>
                      </td>
                      <td *ngFor="let stat of STAT_ORDER">
                        {{ getItemStatWithMasterwork(item, stat) }}
                      </td>
                    </tr>

                    <!-- Total armor stats row -->
                    <tr class="total-row" *ngIf="result.statsNoMods">
                      <td
                        class="total-label expandable-row"
                        [class.expanded]="isBreakdownExpanded(i)"
                        (click)="toggleBreakdown(i); $event.stopPropagation()"
                        *ngIf="hasBreakdownRows(result)">
                        <div class="expandable-content">
                          <strong>Total (armor)</strong>
                          <mat-icon class="expand-icon">{{
                            isBreakdownExpanded(i) ? "expand_less" : "expand_more"
                          }}</mat-icon>
                        </div>
                      </td>
                      <td class="total-label" *ngIf="!hasBreakdownRows(result)">
                        <strong>Total (armor)</strong>
                      </td>
                      <td *ngFor="let stat of STAT_ORDER">
                        <span class="stat-summary-cell">
                          <app-stat-icon [stat]="stat" class="stat-icon-table"></app-stat-icon>
                          <strong>{{ result.statsNoMods[stat] }}</strong>
                        </span>
                      </td>
                    </tr>

                    <!-- Configuration bonus row -->
                    <tr class="config-row" *ngIf="hasConfigBonus() && isBreakdownExpanded(i)">
                      <td class="config-label">Configuration</td>
                      <td *ngFor="let stat of STAT_ORDER">
                        <span
                          *ngIf="getConfigValue(stat) !== 0"
                          [class]="getConfigValue(stat) > 0 ? 'positive' : 'negative'"
                          >{{ getConfigValue(stat) > 0 ? "+" : "" }}{{ getConfigValue(stat) }}</span
                        >
                      </td>
                    </tr>

                    <!-- Minor mods row -->
                    <tr
                      class="minor-mods-row"
                      *ngIf="hasMinorMods(result) && isBreakdownExpanded(i)">
                      <td class="mods-label">Minor Mods</td>
                      <td *ngFor="let stat of STAT_ORDER">
                        <span *ngIf="getMinorModCount(result, stat) > 0" class="positive">
                          {{ getMinorModCount(result, stat) }}×5
                        </span>
                      </td>
                    </tr>

                    <!-- Major mods row -->
                    <tr
                      class="major-mods-row"
                      *ngIf="hasMajorMods(result) && isBreakdownExpanded(i)">
                      <td class="mods-label">Major Mods</td>
                      <td *ngFor="let stat of STAT_ORDER">
                        <span *ngIf="getMajorModCount(result, stat) > 0" class="positive">
                          {{ getMajorModCount(result, stat) }}×10
                        </span>
                      </td>
                    </tr>

                    <!-- Artifice mods row -->
                    <tr
                      class="artifice-mods-row"
                      *ngIf="hasArtificeMods(result) && isBreakdownExpanded(i)">
                      <td class="mods-label">Artifice Mods</td>
                      <td *ngFor="let stat of STAT_ORDER">
                        <span *ngIf="getArtificeModCount(result, stat) > 0" class="positive">
                          {{ getArtificeModCount(result, stat) }}×3
                        </span>
                      </td>
                    </tr>
                    <!-- Tuning stat row -->
                    <tr class="tuning-row" *ngIf="result.tuningStats && isBreakdownExpanded(i)">
                      <td class="tuning-label">Tuning</td>
                      <td *ngFor="let stat of STAT_ORDER">
                        <span *ngIf="result.tuningStats[stat] !== 0" class="tuning-value">
                          <span
                            [class.positive]="result.tuningStats[stat] > 0"
                            [class.negative]="result.tuningStats[stat] < 0">
                            {{ result.tuningStats[stat] > 0 ? "+" : ""
                            }}{{ result.tuningStats[stat] }}
                          </span>
                        </span>
                      </td>
                    </tr>

                    <!-- Final total row -->
                    <tr class="final-total-row">
                      <td class="total-label" style="text-align: left"
                        ><strong>Total (with Mods)</strong></td
                      >
                      <td *ngFor="let stat of STAT_ORDER">
                        <span class="stat-summary-cell">
                          <app-stat-icon [stat]="stat" class="stat-icon-table"></app-stat-icon>
                          <strong>{{ result.stats[stat] }}</strong>
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Actions -->
              <div class="card-actions">
                <button mat-stroked-button (click)="copyDIMQuery(result); $event.stopPropagation()">
                  <mat-icon>content_copy</mat-icon>
                  Copy DIM Query
                </button>
                <button mat-stroked-button (click)="openInDIM(result); $event.stopPropagation()">
                  <mat-icon>open_in_new</mat-icon>
                  Open in DIM
                </button>
                <button
                  mat-stroked-button
                  matTooltip="Warning: Does not move items out of your inventory."
                  (click)="moveItems(result); $event.stopPropagation()">
                  <mat-icon>move_down</mat-icon>
                  Move Items
                </button>
                <button
                  mat-stroked-button
                  matTooltip="Warning: Does not move items out of your inventory."
                  (click)="moveItems(result, true); $event.stopPropagation()">
                  <mat-icon>upload</mat-icon>
                  Equip Items
                </button>
              </div>
            </div>
          </div>
        </div>
      </mat-card>
    </div>

    <!-- Loading indicator -->
    <div class="loading-indicator" *ngIf="isLoading">
      <mat-spinner diameter="32"></mat-spinner>
      <span>Loading more results...</span>
    </div>
  </div>
</div>
